import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useProjects } from '../contexts/ProjectContext';
import { fetchPublicTargets } from '../services/CoreService';
import TargetSelect from './TargetSelect';
import './SideMenu.css';

/**
 * Menu State Types:
 * - 'no-context': No project selected (e.g., /projects page)
 * - 'project-only': Project selected but no target (e.g., /project/:id, /project/:id/settings)
 * - 'project-and-target': Both project and target selected (e.g., /project/:id/target/:name/*)
 * - 'public-project': Public view of project (e.g., /public/:author/:project)
 * - 'public-target': Public view with target selected (e.g., /public/:author/:project/target/:name/*)
 */

const SideMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { projectId, targetName, githubAuthor, githubProject } = useParams();
  const { projects, targets, fetchTargets } = useProjects();

  // Detect public mode from URL path
  const isPublicView = location.pathname.startsWith('/public/');

  // State for public targets (fetched separately from project context)
  const [publicTargetsList, setPublicTargetsList] = useState([]);

  // SessionStorage helpers for tracking last visited project
  const getStoredProject = () => {
    try {
      return sessionStorage.getItem('lastProjectId');
    } catch (e) {
      return null;
    }
  };

  const setStoredProject = (projId) => {
    if (!projId) return;
    try {
      sessionStorage.setItem('lastProjectId', projId);
    } catch (e) {
      // Ignore storage errors
    }
  };

  // SessionStorage helpers for tracking last visited target per project
  const getStoredTarget = (projId) => {
    if (!projId) return null;
    try {
      return sessionStorage.getItem(`lastTarget_${projId}`);
    } catch (e) {
      return null;
    }
  };

  const setStoredTarget = (projId, target) => {
    if (!projId || !target) return;
    try {
      sessionStorage.setItem(`lastTarget_${projId}`, target);
    } catch (e) {
      // Ignore storage errors
    }
  };

  // Save current project and target to sessionStorage when on project/target pages
  useEffect(() => {
    if (projectId) {
      setStoredProject(projectId);
      if (targetName) {
        setStoredTarget(projectId, targetName);
      }
    }
  }, [projectId, targetName]);

  // Fetch targets for the current project if not cached (needed for dropdown)
  useEffect(() => {
    if (projectId && !targets[projectId]) {
      // A failure is recorded in the context (errors.targets[projectId])
      fetchTargets(projectId).catch(() => {});
    }
  }, [projectId, targets, fetchTargets]);

  // Fetch targets for public view (needed for dropdown)
  useEffect(() => {
    if (!isPublicView || !githubAuthor || !githubProject) return;
    let cancelled = false;
    fetchPublicTargets(githubAuthor, githubProject).then(
      result => { if (!cancelled) setPublicTargetsList(result); },
      () => { /* ignore errors */ }
    );
    return () => { cancelled = true; };
  }, [isPublicView, githubAuthor, githubProject]);

  // Check if we're on account settings page (needs stored context)
  const isOnAccountSettingsPage = location.pathname === '/settings';

  // Check if we're on a project page without a target in the URL (needs stored target context)
  const isOnProjectPageWithoutTarget = !isPublicView && projectId && !location.pathname.includes('/target/');

  // Compute the menu state based on current URL
  const menuState = useMemo(() => {
    const path = location.pathname;

    // Public view states
    if (isPublicView) {
      if (path.includes('/target/')) {
        return 'public-target';
      }
      return 'public-project';
    }

    // Target in URL - definitely project-and-target state
    if (path.includes('/target/')) {
      return 'project-and-target';
    }

    // Pages that should maintain stored target context:
    // - Account settings
    // - Project pages without target (project overview, project settings)
    if (isOnAccountSettingsPage || isOnProjectPageWithoutTarget) {
      const effectiveProjId = projectId || getStoredProject();
      const storedTargetName = effectiveProjId ? getStoredTarget(effectiveProjId) : null;

      if (effectiveProjId && storedTargetName) {
        return 'project-and-target';
      }
      if (effectiveProjId) {
        return 'project-only';
      }
      return 'no-context';
    }

    // No project context
    return 'no-context';
  }, [location.pathname, isPublicView, isOnAccountSettingsPage, isOnProjectPageWithoutTarget, projectId]);

  // Get effective project ID based on menu state
  const effectiveProjectId = useMemo(() => {
    if (projectId) return projectId;
    if (isOnAccountSettingsPage) return getStoredProject();
    return null;
  }, [projectId, isOnAccountSettingsPage]);

  // Get effective target name based on menu state
  // Use stored target when on pages that maintain target context
  const effectiveTargetName = useMemo(() => {
    if (targetName) return targetName;
    if ((isOnAccountSettingsPage || isOnProjectPageWithoutTarget) && effectiveProjectId) {
      return getStoredTarget(effectiveProjectId);
    }
    return null;
  }, [targetName, isOnAccountSettingsPage, isOnProjectPageWithoutTarget, effectiveProjectId]);

  // Build URL prefix for public mode
  const urlPrefix = useMemo(() => {
    if (isPublicView && githubAuthor && githubProject && targetName) {
      return `/public/${githubAuthor}/${githubProject}/target/${encodeURIComponent(targetName)}`;
    }
    return null;
  }, [isPublicView, githubAuthor, githubProject, targetName]);

  // Get commit params from URL when on Compare or Symbols pages
  const commitQueryString = useMemo(() => {
    const isOnCompareOrSymbols = location.pathname.endsWith('/compare') || location.pathname.endsWith('/symbols');
    if (!isOnCompareOrSymbols) return '';

    const base = searchParams.get('base');
    const head = searchParams.get('head');

    if (base && head) {
      return `?base=${encodeURIComponent(base)}&head=${encodeURIComponent(head)}`;
    }
    return '';
  }, [location.pathname, searchParams]);

  // Build dashboard path based on menu state
  const dashboardPath = useMemo(() => {
    if (isPublicView && urlPrefix) {
      return urlPrefix;
    }
    if (effectiveProjectId && effectiveTargetName) {
      return `/project/${effectiveProjectId}/target/${encodeURIComponent(effectiveTargetName)}`;
    }
    // Fallback: use first available target
    if (effectiveProjectId && targets[effectiveProjectId]?.length > 0) {
      const firstTarget = targets[effectiveProjectId][0];
      return `/project/${effectiveProjectId}/target/${encodeURIComponent(firstTarget.name)}`;
    }
    return null;
  }, [isPublicView, urlPrefix, effectiveProjectId, effectiveTargetName, targets]);

  // Get current project data (not available in public mode)
  const currentProject = useMemo(() => {
    if (isPublicView) return null;
    return projects?.find(p => String(p.id) === String(effectiveProjectId));
  }, [isPublicView, projects, effectiveProjectId]);

  // Display names based on menu state
  const displayProjectName = isPublicView ? githubProject : currentProject?.name;
  const displayTargetName = useMemo(() => {
    // Only show target name when in target state
    if (menuState === 'project-and-target' || menuState === 'public-target') {
      return effectiveTargetName ? decodeURIComponent(effectiveTargetName) : null;
    }
    return null;
  }, [menuState, effectiveTargetName]);

  // Get available targets for the current project (for dropdown)
  const availableTargets = useMemo(() => {
    if (isPublicView) return publicTargetsList;
    if (!effectiveProjectId) return [];
    return targets[effectiveProjectId] || [];
  }, [isPublicView, publicTargetsList, effectiveProjectId, targets]);

  // Determine current page suffix from the URL (e.g., '/compare', '/symbols', '/budgets', '/region-config', or '')
  const currentPageSuffix = useMemo(() => {
    const path = location.pathname;
    const suffixes = ['/compare', '/symbols', '/budgets', '/region-config'];
    for (const suffix of suffixes) {
      if (path.endsWith(suffix)) return suffix;
    }
    return '';
  }, [location.pathname]);

  // Handle target switch from dropdown
  const handleTargetSwitch = useCallback((newTargetName) => {
    if (!newTargetName || newTargetName === displayTargetName) return;

    const encodedTarget = encodeURIComponent(newTargetName);
    const queryString = location.search;

    let basePath;
    if (isPublicView && githubAuthor && githubProject) {
      basePath = `/public/${githubAuthor}/${githubProject}/target/${encodedTarget}`;
    } else if (effectiveProjectId) {
      basePath = `/project/${effectiveProjectId}/target/${encodedTarget}`;
    } else {
      return;
    }

    navigate(`${basePath}${currentPageSuffix}${queryString}`);
  }, [displayTargetName, effectiveProjectId, isPublicView, githubAuthor, githubProject, currentPageSuffix, location.search, navigate]);

  // Derived booleans from menu state for cleaner rendering
  const showTargetItems = menuState === 'project-and-target' || menuState === 'public-target';
  const showProjectContext = menuState !== 'no-context';
  const showProjectSettings = !isPublicView && showProjectContext && effectiveProjectId;

  const clearStoredContext = () => {
    try {
      const storedProjectId = getStoredProject();
      sessionStorage.removeItem('lastProjectId');
      if (storedProjectId) {
        sessionStorage.removeItem(`lastTarget_${storedProjectId}`);
      }
    } catch (e) {
      // Ignore storage errors
    }
  };

  // Clear only the stored target for a project (keeps project context)
  const clearStoredTarget = (projId) => {
    if (!projId) return;
    try {
      sessionStorage.removeItem(`lastTarget_${projId}`);
    } catch (e) {
      // Ignore storage errors
    }
  };

  const handleNavigation = (path) => {
    const isCurrentPage = location.pathname === path;

    // Clear stored project/target context when navigating to Projects page
    if (path === '/projects') {
      clearStoredContext();
    }

    // If clicking on the currently active page, dispatch reload event
    if (isCurrentPage) {
      window.dispatchEvent(new CustomEvent('pageReload', {
        detail: { path }
      }));
    } else {
      navigate(path);
    }
  };

  return (
    <div className="side-menu">
      <div className="side-menu-content">
        {/* Projects link (hidden for public view) */}
        {!isPublicView && (
          <button
            onClick={() => handleNavigation('/projects')}
            className={`side-menu-item ${location.pathname === '/projects' ? 'active' : ''}`}
            title="Projects"
          >
            <span className="side-menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="side-menu-label">Projects</span>
          </button>
        )}

        {/* Project link (visible when project context exists, clears target context) */}
        {!isPublicView && showProjectContext && effectiveProjectId && (
          <button
            onClick={() => {
              clearStoredTarget(effectiveProjectId);
              handleNavigation(`/project/${effectiveProjectId}`);
            }}
            className={`side-menu-item ${location.pathname === `/project/${effectiveProjectId}` ? 'active' : ''}`}
            title="Project"
          >
            <span className="side-menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="side-menu-label">Project</span>
          </button>
        )}

        {/* Public Project link (only for public view) */}
        {isPublicView && githubAuthor && githubProject && (
          <button
            onClick={() => handleNavigation(`/public/${githubAuthor}/${githubProject}`)}
            className={`side-menu-item ${location.pathname === `/public/${githubAuthor}/${githubProject}` ? 'active' : ''}`}
            title="Project"
          >
            <span className="side-menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="side-menu-label">Project</span>
          </button>
        )}

        {/* Project and Target Info */}
        {showProjectContext && displayProjectName && (
          <div className="side-menu-context">
            <div className="side-menu-context-section">
              <div className="side-menu-context-label">Project:</div>
              <div className="side-menu-context-value" title={displayProjectName}>
                {displayProjectName}
              </div>
            </div>
            {displayTargetName && (
              <div className="side-menu-context-section">
                <div className="side-menu-context-label">Target:</div>
                {availableTargets.length > 1 ? (
                  <TargetSelect
                    targets={availableTargets}
                    value={displayTargetName}
                    onChange={handleTargetSwitch}
                    className="side-menu-target-select"
                  />
                ) : (
                  <div className="side-menu-context-value" title={displayTargetName}>
                    {displayTargetName}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Dashboard link (only when target context available) */}
        {showTargetItems && dashboardPath && (
          <button
            onClick={() => handleNavigation(dashboardPath)}
            className={`side-menu-item ${location.pathname === dashboardPath ? 'active' : ''}`}
            title="Dashboard"
          >
            <span className="side-menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="14" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="side-menu-label">Dashboard</span>
          </button>
        )}

        {/* Build Comparison link (only when target context available) */}
        {showTargetItems && (isPublicView ? urlPrefix : (effectiveProjectId && effectiveTargetName)) && (
          <button
            onClick={() => handleNavigation(
              isPublicView
                ? `${urlPrefix}/compare${commitQueryString}`
                : `/project/${effectiveProjectId}/target/${encodeURIComponent(effectiveTargetName)}/compare${commitQueryString}`
            )}
            className={`side-menu-item ${location.pathname.endsWith('/compare') ? 'active' : ''}`}
            title="Build Comparison"
          >
            <span className="side-menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 12H9M15 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="side-menu-label">Compare Builds</span>
          </button>
        )}

        {/* Symbol Analysis link (only when target context available) */}
        {showTargetItems && (isPublicView ? urlPrefix : (effectiveProjectId && effectiveTargetName)) && (
          <button
            onClick={() => handleNavigation(
              isPublicView
                ? `${urlPrefix}/symbols${commitQueryString}`
                : `/project/${effectiveProjectId}/target/${encodeURIComponent(effectiveTargetName)}/symbols${commitQueryString}`
            )}
            className={`side-menu-item ${location.pathname.endsWith('/symbols') ? 'active' : ''}`}
            title="Symbol Analysis"
          >
            <span className="side-menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1" stroke="currentColor" strokeWidth="2"/>
                <circle cx="12" cy="12" r="1" fill="currentColor"/>
              </svg>
            </span>
            <span className="side-menu-label">Symbol Analysis</span>
          </button>
        )}

        {/* Budgets link (only when target context available) */}
        {showTargetItems && (isPublicView ? urlPrefix : (effectiveProjectId && effectiveTargetName)) && (
          <button
            onClick={() => handleNavigation(
              isPublicView
                ? `${urlPrefix}/budgets`
                : `/project/${effectiveProjectId}/target/${encodeURIComponent(effectiveTargetName)}/budgets`
            )}
            className={`side-menu-item ${location.pathname.endsWith('/budgets') ? 'active' : ''}`}
            title="Memory Budgets"
          >
            <span className="side-menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="6" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                <path d="M3 10H21" stroke="currentColor" strokeWidth="2"/>
                <circle cx="17" cy="14" r="1.5" fill="currentColor"/>
                <path d="M6 6L8 2L16 2L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
            <span className="side-menu-label">Budgets</span>
          </button>
        )}

        {/* Region Configuration link (only when target context available) */}
        {showTargetItems && (isPublicView ? urlPrefix : (effectiveProjectId && effectiveTargetName)) && (
          <button
            onClick={() => handleNavigation(
              isPublicView
                ? `${urlPrefix}/region-config`
                : `/project/${effectiveProjectId}/target/${encodeURIComponent(effectiveTargetName)}/region-config`
            )}
            className={`side-menu-item ${location.pathname.endsWith('/region-config') ? 'active' : ''}`}
            title="Region Configuration"
          >
            <span className="side-menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" rx="1"/>
                <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" rx="1"/>
                <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" rx="1"/>
                <path d="M14 17.5H21M17.5 14V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="side-menu-label">Region Config</span>
          </button>
        )}

        {/* Project Settings link (when project context available, not in public view) */}
        {showProjectSettings && (
          <button
            onClick={() => handleNavigation(`/project/${effectiveProjectId}/settings`)}
            className={`side-menu-item ${location.pathname.includes('/project/') && location.pathname.endsWith('/settings') ? 'active' : ''}`}
            title="Project Settings"
          >
            <span className="side-menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.5797 9.96512 20.2585 9.77251 19.9887C9.5799 19.7189 9.31074 19.5141 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.4203 10.0723 3.74151 9.96512 4.0113 9.77251C4.28109 9.5799 4.48594 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72669 4.07447C9.90537 3.80618 10.0007 3.49179 10.002 3.17V3C10.002 2.46957 10.2127 1.96086 10.5878 1.58579C10.9629 1.21071 11.4716 1 12.002 1C12.5324 1 13.0411 1.21071 13.4162 1.58579C13.7913 1.96086 14.002 2.46957 14.002 3V3.09C14.0097 3.4203 14.1169 3.74151 14.3095 4.0113C14.5021 4.28109 14.7713 4.48594 15.082 4.6C15.3836 4.73312 15.7182 4.77282 16.0426 4.714C16.367 4.65519 16.6663 4.50054 16.902 4.27L16.962 4.21C17.1477 4.02405 17.3683 3.87653 17.6111 3.77588C17.8539 3.67523 18.1141 3.62343 18.377 3.62343C18.6398 3.62343 18.9001 3.67523 19.1429 3.77588C19.3857 3.87653 19.6062 4.02405 19.792 4.21C19.9779 4.39575 20.1255 4.61632 20.2261 4.85912C20.3268 5.10192 20.3786 5.36217 20.3786 5.625C20.3786 5.88783 20.3268 6.14808 20.2261 6.39088C20.1255 6.63368 19.9779 6.85425 19.792 7.04L19.732 7.1C19.5015 7.33568 19.3468 7.63502 19.288 7.95941C19.2292 8.28381 19.2689 8.61838 19.402 8.92V9C19.5288 9.29577 19.7392 9.54802 20.0075 9.72669C20.2758 9.90537 20.5902 10.0007 20.912 10.002H21C21.5304 10.002 22.0391 10.2127 22.4142 10.5878C22.7893 10.9629 23 11.4716 23 12.002C23 12.5324 22.7893 13.0411 22.4142 13.4162C22.0391 13.7913 21.5304 14.002 21 14.002H20.91C20.5797 14.0097 20.2585 14.1169 19.9887 14.3095C19.7189 14.5021 19.5141 14.7713 19.4 15.082Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="side-menu-label">Project Settings</span>
          </button>
        )}

        {/* Account Settings link (hidden for public view) */}
        {!isPublicView && (
          <button
            onClick={() => handleNavigation('/settings')}
            className={`side-menu-item ${location.pathname === '/settings' ? 'active' : ''}`}
            title="Account Settings"
          >
            <span className="side-menu-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="side-menu-label">Account Settings</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default SideMenu;